import EditOutlined from "@ant-design/icons/lib/icons/EditOutlined";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import * as Sentry from "@sentry/browser";
import { Button, Col, Row, Table, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { RefObject } from "react";
import { NavLink } from "react-router-dom";
import BaseForm from "../components/Form/BaseForm";
import entitlements from "../config/entitlements";
import { UPDATE_NOTE } from "../gql/notes";
import { Customer, FormFieldConfig, Note } from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import EntitledSection from "./EntitledSection";
import { TicketCreateForm } from "./InteractionSection";
import ModalCreator from "./ModalCreator";

const user = JSON.parse(localStorage.getItem("user") as string);

type BaseProps = {
  showHeading: boolean;
  notes: Note[];
  entity: string;
  createNote: () => Promise<void>;
  reference: RefObject<FormInstance>;
  showForm: boolean;
  canCreateTicket?: false;
  stackFormButton?: boolean;
  style?: React.CSSProperties;
  onNoteUpdate?: (note: Note[]) => void;
};

type TicketPresentProps = {
  showHeading: boolean;
  notes: Note[];
  createNote: () => Promise<void>;
  reference: RefObject<FormInstance>;
  onNoteUpdate?: (notes: Note[]) => void;
  showForm: boolean;
  canCreateTicket: true;
  showTicketForm: boolean;
  toggleTicketCreate: (id: string) => void;
  customer?: Customer;
  ticketReference: RefObject<FormInstance>;
  ticketLoading: boolean;
  createTicket: () => Promise<void>;
  style?: React.CSSProperties;
  currentNote?: Note;
  entity: string;
  stackFormButton?: boolean;
};

type Props = BaseProps | TicketPresentProps;

interface State {
  notes: Note[];
  isLoading: boolean;
  isEditingNote: boolean;
  isCreatingNote: boolean;
  currentNote: Note | undefined;
}

export const generateNoteFormFields = ({
  createNote,
  updateNote,
  isEditingNote,
  currentNote,
  stackButton,
}: {
  createNote: () => void;
  updateNote: () => void;
  isEditingNote: boolean;
  currentNote: Note | undefined;
  stackButton?: boolean;
}): FormFieldConfig[] => {
  const baseButtonStyle = {
    borderRadius: 0,
    height: "33px",
  };
  const buttonStyle = stackButton
    ? { ...baseButtonStyle, top: 0, marginTop: "8px" }
    : { ...baseButtonStyle, top: "29px" };

  return [
    {
      id: "description",
      name: "description",
      required: true,
      inputType: "textArea",
      rule: "string",
      label: "Description",
      placeholder: "Enter description",
      initialValue:
        isEditingNote && currentNote ? currentNote?.description : "",
      colConfig: {
        span: stackButton ? 24 : 19,
      },
    },
    {
      name: `${
        isEditingNote && currentNote ? "updateNoteBtn" : "createNoteBtn"
      }`,
      value: `${isEditingNote && currentNote ? "Update Note" : "Add Note"}`,
      inputType: "button",
      required: false,
      elementStyle: buttonStyle,
      className: "base-button",
      handleClick: isEditingNote && currentNote ? updateNote : createNote,
      colConfig: {
        offset: stackButton ? 0 : 1,
        span: stackButton ? 6 : 4,
      },
    },
  ];
};

export function getColumns<T extends Note = Note>(
  toggleEditNote: (note: Note) => void,
): ColumnType<T>[] {
  return [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render(text: string, record: Note): JSX.Element {
        return (
          <span>
            {record.createdAt
              ? moment(record.createdAt, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "date",
      render(text: string, record: Note): JSX.Element {
        return (
          <span>
            {record.updatedAt
              ? moment(record.updatedAt, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render(text: string, record: Note): JSX.Element {
        return (
          <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
            {record.description || record.note?.description}
          </span>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "user",
      key: "User",
      render(text: string, record: Note): JSX.Element {
        return <span>{record.user?.name || record.note?.user?.name}</span>;
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      render(text: string, record: Note): JSX.Element {
        return (
          <span>
            <NavLink
              to="#"
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
              ): void => {
                e.preventDefault();
                toggleEditNote(record.note ?? record);
              }}
            >
              <EditOutlined />
            </NavLink>
          </span>
        );
      },
    },
  ];
}

interface BaseNoteProps {
  data: Note[];
  showTicket?: false;
  toggleEditNote: (note: Note | undefined) => void;
}

interface TicketPresentNoteProps {
  data: Note[];
  showTicket: true;
  toggleEditNote: (note: Note | undefined) => void;
  toggleCreateTicket: (id: string) => void;
}

type NoteProps = BaseNoteProps | TicketPresentNoteProps;

export function TicketColumn(props: {
  toggleCreateTicket: (id: string) => void;
  toggleEditNote: (note: Note | undefined) => void;
}) {
  const toggleCreateTicket = props.toggleCreateTicket;
  return [
    ...getColumns(props.toggleEditNote),
    {
      title: "Tickets",
      dataIndex: "tickets",
      key: "tickets",
      render(text: string, record: Note): JSX.Element {
        return (
          <div>
            {record.tickets?.map((noteTicket) => (
              <p key={noteTicket?.id}>
                {noteTicket?.ticket?.url ? (
                  <a
                    href={noteTicket.ticket.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {noteTicket.ticket.url}
                  </a>
                ) : (
                  "No ticket url available"
                )}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render(text: string, record: Note): JSX.Element {
        return (
          <NavLink
            id="ticket-btn"
            to="#"
            onClick={(
              e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
            ): void => {
              e.preventDefault();
              toggleCreateTicket(record.referenceId as string);
            }}
            style={{
              marginRight: "10px",
            }}
          >
            Create Ticket
          </NavLink>
        );
      },
    },
  ];
}

export class NotesTableFactory extends React.Component<NoteProps> {
  render() {
    const tempData: Note[] = this.props.data.map((note: Note): Note => {
      return { ...note, key: note.id };
    });

    const modifiedColumns = this.props.showTicket
      ? TicketColumn({
          toggleCreateTicket: this.props.toggleCreateTicket,
          toggleEditNote: this.props.toggleEditNote,
        })
      : getColumns(this.props.toggleEditNote);

    return (
      <Table
        bordered={true}
        columns={modifiedColumns}
        dataSource={tempData}
        pagination={false}
      />
    );
  }
}

export default class NotesSection extends React.Component<Props, State> {
  updateNoteReference: RefObject<FormInstance>;
  constructor(props: Props) {
    super(props);
    this.state = {
      notes: [],
      isLoading: false,
      isEditingNote: false,
      isCreatingNote: false,
      currentNote: undefined,
    };

    this.updateNoteReference = React.createRef<FormInstance>();
  }

  openAddNoteModal = () => {
    this.setState({ isCreatingNote: true });
  };

  closeAddNoteModal = () => {
    this.setState({ isCreatingNote: false });
  };

  handleCreateNote = async () => {
    await this.props.createNote();
    this.closeAddNoteModal();
  };

  toggleEditNote = (note: Note | undefined) => {
    this.setState((prevState) => ({
      currentNote: note,
      isEditingNote: !prevState.isEditingNote,
    }));
  };

  closeEditNoteModal = () => {
    this.setState((prevState) => ({
      currentNote: prevState.isEditingNote ? undefined : prevState.currentNote,
      isEditingNote: false,
    }));
  };

  updateNoteList = (newNotes: Note[]) => {
    if (this.props.onNoteUpdate) {
      this.props.onNoteUpdate(newNotes);
    }
  };

  updateNote = async (): Promise<void> => {
    const { currentNote } = this.state;

    interface Response {
      notes: Note[];
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.updateNoteReference.current) return;

    let data;

    try {
      data = await this.updateNoteReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    if (!currentNote?.id) {
      message.error("Unable to update note. Please try again.");
      return;
    }

    this.setState({ isLoading: true });

    let variables = {
      noteId: currentNote?.id,
      description: data.description,
      userId: user.id,
      entity: this.props.entity,
    };

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_NOTE,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateNote",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        this.updateNoteList(mutation.notes);
        message.success("Note updated successfully");
      }

      this.setState({
        currentNote: undefined,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false, isEditingNote: false });
  };

  async componentDidMount(): Promise<void> {
    if (this.props.notes) {
      this.setState({
        notes: this.props.notes,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.notes !== this.props.notes) {
      this.setState({
        notes: this.props.notes,
      });
    }
  }

  render(): JSX.Element {
    const noteFields = generateNoteFormFields({
      createNote: this.handleCreateNote,
      updateNote: this.updateNote,
      isEditingNote: false,
      currentNote: undefined,
      stackButton: true,
    });

    const editNoteFields = generateNoteFormFields({
      createNote: this.props.createNote,
      updateNote: this.updateNote,
      isEditingNote: true,
      currentNote: this.state.currentNote,
      stackButton: true,
    });

    return (
      <div style={{ ...this.props.style }}>
        {this.props.showHeading && (
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: "20px" }}
          >
            <h1
              style={{
                marginBottom: 0,
                color: "rgba(0, 0, 0, 0.85)",
                fontWeight: "bold",
                fontSize: "16px",
                lineHeight: "1.5",
              }}
            >
              Notes
            </h1>
            {this.props.showForm && (
              <Button
                className="base-button"
                icon={<PlusOutlined />}
                onClick={this.openAddNoteModal}
              >
                Add Note
              </Button>
            )}
          </Row>
        )}
        {this.props?.notes?.length > 0 && (
          <div
            style={{
              marginBottom: "18px",
            }}
          >
            <Col>
              {this.props.canCreateTicket ? (
                <NotesTableFactory
                  data={this.state.notes}
                  showTicket={true}
                  toggleCreateTicket={this.props.toggleTicketCreate}
                  toggleEditNote={this.toggleEditNote}
                />
              ) : (
                <NotesTableFactory
                  data={this.state.notes}
                  toggleEditNote={this.toggleEditNote}
                />
              )}
            </Col>
          </div>
        )}

        {this.props.canCreateTicket && this.props.showTicketForm && (
          <EntitledSection
            entitlement={entitlements.BusinessDevelopment.interactions.list}
          >
            {ModalCreator({
              title: "Create Ticket",
              okType: "primary",
              okText: "Close",
              width: 900,
              showModal: this.props.showTicketForm,
              onOkay: (): void => {
                if (this.props.canCreateTicket) {
                  this.props.toggleTicketCreate(
                    this.props.currentNote?.referenceId ?? "",
                  );
                }
              },
              toggleModal: (): void => {
                if (this.props.canCreateTicket) {
                  this.props.toggleTicketCreate(
                    this.props.currentNote?.referenceId ?? "",
                  );
                }
              },
              showFooter: false,
              inscription: (
                <TicketCreateForm
                  reference={this.props.ticketReference}
                  customer={this.props.customer}
                  loading={this.props.ticketLoading}
                  createTicket={this.props.createTicket}
                  currentNote={this.props.currentNote}
                />
              ),
            })}
          </EntitledSection>
        )}

        {ModalCreator({
          title: "Add Note",
          okType: "primary",
          okText: "Close",
          width: 700,
          showModal: this.state.isCreatingNote,
          onOkay: this.closeAddNoteModal,
          toggleModal: this.closeAddNoteModal,
          showFooter: false,
          inscription: (
            <BaseForm
              layout="vertical"
              reference={this.props.reference}
              fields={noteFields}
              fieldsToHide={[]}
            ></BaseForm>
          ),
        })}

        {ModalCreator({
          title: "Edit Note",
          okType: "primary",
          okText: "Close",
          width: 700,
          showModal: this.state.isEditingNote,
          onOkay: this.closeEditNoteModal,
          toggleModal: this.closeEditNoteModal,
          showFooter: false,
          inscription: (
            <BaseForm
              key={this.state.currentNote?.id || "edit-note"}
              layout="vertical"
              reference={this.updateNoteReference}
              fields={editNoteFields}
              fieldsToHide={[]}
            ></BaseForm>
          ),
        })}
      </div>
    );
  }
}
